import React, { useCallback, useEffect, useState } from 'react';
import _ from 'lodash';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import styles from '../styles/pages/Purchase.module.css';
import * as braintree from 'braintree-web';
import { client, hostedFields } from 'braintree-web';
import { ActionButton } from '@ax2/common/components/ActionButton';
import PanelType, { getPanelTypeText } from '@ax2/common/model/PanelType';

import {
  countTotal,
  getClientToken,
  getOrder,
  payPurchase,
  Order,
  products,
  createPendingPurchase,
  saveOrder,
  getTotal,
  OrderItem,
} from '../rest/shopClient';
import DefaultPage from './DefaultPage';
import Modal from '@ax2/common/components/Modal';
import Box from '../components/Box';
import Dropdown, { DropdownOption } from '@ax2/common/components/Dropdown';
import useMediaQuery from '@ax2/common/helpers/hooks/useMediaQuery';
import AnimateHeight from 'react-animate-height';
import { formatUSD } from '../helpers/format';
import checkBlueCircle from '../assets/check_blue_circle.svg';
import * as Yup from 'yup';
import { Formik } from 'formik-v2';
import { physicianOptions } from '../helpers/physicianOptions';
import TextField from '../components/form/TextField';
import { HostedFieldsStateObject } from 'braintree-web';
import visa from '../assets/cards/visa.svg';
import downIcon from '@ax2/common/assets/chevron_down_blue.svg';
import mastercard from '../assets/cards/mastercard.svg';
import americanExpress from '../assets/cards/american_express.svg';
import cjb from '../assets/cards/cjb.svg';
import discover from '../assets/cards/discover.svg';
import config from '../config';
import useDebounce from '@ax2/common/helpers/hooks/useDobounce';
import Loading from '@ax2/common/components/Loading';
import { SupportRequestType } from '../model/SupportRequest';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';
import { useSurveyContext } from 'contexts/SurveyContext';

type PurchaseStep = 1 | 2 | 3 | 4;

const stepUrlIds = {
  1: 'orderSummary',
  2: 'paymentDetails',
  3: 'placeOrder',
  4: 'thankYou',
};

function getQtyOptions(qty: number): DropdownOption<number>[] {
  return _.range(0, Math.max(6, qty + 1)).map(n => ({
    title: n.toString(),
    value: n,
  }));
}

interface OrderTableRowProps {
  panelType: PanelType;
  qty: number;
  updateQty: (panelType: PanelType, qty: number) => void;
  smallScreen: boolean;
  setIsPhysicianSpecialtyOpen: (value: boolean) => void;
}

const OrderTableRow: React.FC<OrderTableRowProps> = ({
  panelType,
  qty,
  updateQty,
  smallScreen = false,
  setIsPhysicianSpecialtyOpen,
}) => {
  if (smallScreen) {
    return (
      <tr>
        <td colSpan={4}>
          <p className={styles.tableProductTitle}>
            {getPanelTypeText(panelType) + ' Panel Survey'}
          </p>
          <p className={styles.tableProductSubtitle}>
            For {getPanelTypeText(panelType) + ' Panel'}
          </p>
          <div className={styles.qtyAndPriceInline}>
            <div className={styles.tableQtyDropdownWrapper}>
              {panelType === 'PhysicianSurvey' ? (
                <div
                  className={styles.btnPhysicianSpecial}
                  onClick={() => setIsPhysicianSpecialtyOpen(true)}
                >
                  {qty}
                  <img src={downIcon} />
                </div>
              ) : (
                <Dropdown
                  options={getQtyOptions(qty)}
                  value={qty}
                  onChange={value => {
                    updateQty(panelType, value);
                  }}
                />
              )}
            </div>
            <p className={styles.tableProductPrice}>
              &times; ${formatUSD(products[panelType].price)}
            </p>
          </div>
          <p className={styles.tableText}>Contact us if...</p>
          <p className={styles.tableText}>
            You need to create a PO/be invoiced.{' '}
            <Link to={'/support?t=' + SupportRequestType.Invoice}>
              Contact us.
            </Link>
          </p>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <p className={styles.tableProductTitle}>
          {getPanelTypeText(panelType) + ' Panel Survey'}
        </p>
        <p className={styles.tableProductSubtitle}>
          For {getPanelTypeText(panelType) + ' Panel'}
        </p>
      </td>
      <td className={styles.tableQtyDropdownWrapper}>
        {panelType === 'PhysicianSurvey' ? (
          <div
            className={styles.btnPhysicianSpecial}
            onClick={() => setIsPhysicianSpecialtyOpen(true)}
          >
            {qty}
            <img src={downIcon} />
          </div>
        ) : (
          <Dropdown
            options={getQtyOptions(qty)}
            value={qty}
            onChange={value => updateQty(panelType, value)}
          />
        )}
      </td>
      <td>
        <p className={styles.tableText}>
          You need to create a PO/be invoiced.{' '}
          <Link to={'/support?t=' + SupportRequestType.Invoice}>
            Contact us.
          </Link>
        </p>
      </td>
      <td>
        <span className={styles.tableProductPrice}>
          ${formatUSD(products[panelType].price)}
        </span>
      </td>
    </tr>
  );
};

interface DiscountRowProps {
  smallScreen: boolean;
  order: Order;
  setSubtotal: (value: number) => void;
  setDiscount: (value: number) => void;
  setTotal: (value: number) => void;
  setValidCoupon: (value: string) => void;
}

const DiscountRow: React.FC<DiscountRowProps> = ({
  smallScreen,
  order,
  setSubtotal,
  setDiscount,
  setTotal,
  setValidCoupon,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [coupon, setCoupon] = useState<string | null>(null);
  const debouncedCoupon = useDebounce<string | null>(coupon, 200);

  useEffect(() => {
    setTotal(countTotal(order));
    getTotal(order)
      .then(r => {
        setSubtotal(r.itemsAmount);
        setDiscount(r.discount);
        setTotal(r.totalAmount);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [order, setDiscount, setSubtotal, setTotal]);

  useEffect(() => {
    const coupon =
      debouncedCoupon && !_.isEmpty(_.trim(debouncedCoupon))
        ? _.trim(debouncedCoupon)
        : undefined;
    setError(null);
    setLoading(true);
    getTotal(order, coupon)
      .then(r => {
        setSubtotal(r.itemsAmount);
        setDiscount(r.discount);
        setTotal(r.totalAmount);
        setLoading(false);
        coupon && setValidCoupon(coupon);
      })
      .catch(() => {
        coupon && setError('Invalid or expired coupon');
        setLoading(false);
      });
  }, [
    debouncedCoupon,
    order,
    setDiscount,
    setSubtotal,
    setTotal,
    setValidCoupon,
  ]);

  const discountOnChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCoupon(event.target.value);

  const Check = () => {
    return debouncedCoupon && !error && !loading ? (
      <img src={checkBlueCircle} aria-hidden={true} alt="Check mark" />
    ) : null;
  };

  const Error = () => {
    return error ? <span className={styles.discountError}>{error}</span> : null;
  };

  const Loading = () => {
    return loading ? (
      <span className={styles.discountLoading}>Loading...</span>
    ) : null;
  };

  const inputClasses = [
    styles.discountField,
    error ? styles.discountFieldWithError : '',
  ].join(' ');

  if (smallScreen) {
    return (
      <tr>
        <td colSpan={4}>
          <p className={styles.tableProductTitle}>Company Code</p>
          <p className={styles.tableProductSubtitle}>(if applicable)</p>
          <div className={styles.formInline}>
            <div className={styles.discountFieldWrapper}>
              <input className={inputClasses} onChange={discountOnChange} />
              <Check />
              <Error />
              <Loading />
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <p className={styles.tableProductTitle}>Company Code</p>
        <p className={styles.tableProductSubtitle}>(if applicable)</p>
      </td>
      <td className={styles.tableQtyDropdownWrapper} colSpan={3}>
        <div className={styles.discountFieldWrapper}>
          <input className={inputClasses} onChange={discountOnChange} />
          <Check />
          <Error />
          <Loading />
        </div>
      </td>
    </tr>
  );
};

interface OrderTableProps {
  smallScreen: boolean;
  order: Order;
  updateQty: (key: PanelType, value: number) => void;
  subtotal: number;
  setSubtotal: (value: number) => void;
  discount: number;
  setDiscount: (value: number) => void;
  total: number;
  setTotal: (value: number) => void;
  setValidCoupon: (value: string) => void;
  setIsPhysicianSpecialtyOpen: (value: boolean) => void;
}

const OrderTable: React.FC<OrderTableProps> = ({
  smallScreen,
  order,
  updateQty,
  subtotal,
  setSubtotal,
  discount,
  setDiscount,
  total,
  setTotal,
  setValidCoupon,
  setIsPhysicianSpecialtyOpen,
}) => {
  const orderGroupedByPanel = _.groupBy(order, x => x.panelType);
  const panelItems = _.keys(orderGroupedByPanel).map(k => {
    const items = orderGroupedByPanel[k];
    const qty = items.reduce((acc, x) => acc + x.quantity, 0);
    return { panel: k, quantity: qty };
  });
  const sortedPanelItems = _.sortBy(panelItems, x => x.panel);

  return (
    <table className={styles.purchaseTable}>
      <thead>
        {smallScreen ? (
          <tr>
            <th colSpan={4} className={styles.purchaseTableTh}>
              Panel Surveys
            </th>
          </tr>
        ) : (
          <tr>
            <th className={styles.purchaseTableTh}>Panel Surveys</th>
            <th className={styles.purchaseTableTh}>Quantity</th>
            <th className={styles.purchaseTableTh}>Contact us if...</th>
            <th style={{ width: 120 }} className={styles.purchaseTableTh}>
              Price
            </th>
          </tr>
        )}
      </thead>
      <tbody>
        {sortedPanelItems.map(i => (
          <OrderTableRow
            key={i.panel}
            panelType={i.panel as PanelType}
            qty={i.quantity}
            updateQty={updateQty}
            smallScreen={smallScreen}
            setIsPhysicianSpecialtyOpen={setIsPhysicianSpecialtyOpen}
          />
        ))}
        <DiscountRow
          smallScreen={smallScreen}
          order={order}
          setSubtotal={setSubtotal}
          setDiscount={setDiscount}
          setTotal={setTotal}
          setValidCoupon={setValidCoupon}
        />
      </tbody>
      <tfoot className={styles.tableFooter}>
        <tr>
          <td colSpan={3} className={styles.tableAlignRight}>
            <span className={styles.tableFooterTitle}>Subtotal</span>
          </td>
          <td>
            <span className={styles.tableFooterValue}>
              ${formatUSD(subtotal)}
            </span>
          </td>
        </tr>
        {discount > 0 && (
          <tr>
            <td colSpan={3} className={styles.tableAlignRight}>
              <span className={styles.tableFooterTitle}>Discount</span>
            </td>
            <td>
              <span className={styles.tableFooterValue}>
                ${formatUSD(discount)}
              </span>
            </td>
          </tr>
        )}
        <tr>
          <td colSpan={3} className={styles.tableAlignRight}>
            <span className={styles.tableFooterTitleBig}>Total</span>
          </td>
          <td>
            <span className={styles.tableFooterValueBig}>
              ${formatUSD(total)}
            </span>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

interface StepHeaderProps {
  title: string;
  isActive: boolean;
  isComplete: boolean;
  isEditable: boolean;
  onEditClick: () => void;
}

const StepHeader: React.FC<StepHeaderProps> = ({
  title,
  isActive,
  isComplete,
  isEditable,
  onEditClick,
}) => {
  return (
    <div
      className={[
        styles.stepHeaderWrapper,
        isActive ? styles.stepHeaderWrapperActive : '',
      ].join(' ')}
    >
      <h4
        className={[
          styles.stepHeader,
          isActive ? styles.stepHeaderActive : '',
        ].join(' ')}
      >
        {title}
      </h4>
      <div className={styles.stepHeaderEditControls}>
        {isEditable && (
          <ActionButton
            onClick={onEditClick}
            text="Edit"
            size="small"
            type="secondary"
            shadowNormal="1"
            shadowHover="2"
            shadowDisabled="none"
          />
        )}
        {isComplete && (
          <img src={checkBlueCircle} alt="Step Complete" aria-hidden={true} />
        )}
      </div>
    </div>
  );
};

interface PurchaseFormValues {
  cardholderName: string;
  streetAddress: string | null;
  company: string | null;
  locality: string | null;
  region: string | null;
  phone: string | null;
}

const purchaseFormValidationSchema = Yup.object<PurchaseFormValues>().shape({
  cardholderName: Yup.string()
    .uppercase('Use uppercase letters exactly as written on the card')
    .matches(
      /^\s?\w+(\s\w+)+\s?$/,
      'Enter the name exactly as written on the card'
    )
    .required('This field is required'),
  streetAddress: Yup.string()
    .min(3, 'Too short')
    .notRequired(),
  company: Yup.string()
    .min(3, 'Too short')
    .notRequired(),
  locality: Yup.string()
    .min(3, 'Too short')
    .notRequired(),
  region: Yup.string()
    .uppercase('Use uppercase letters, e.g. "FL", "TX"')
    .length(2, 'Use 2-letter state code, such as "FL", "TX"')
    .notRequired(),
  phone: Yup.string().notRequired(),
});

interface HostedFieldsValidity {
  number: boolean;
  expirationDate: boolean;
  cvv: boolean;
  postalCode: boolean;
}

function isBlank(str: string | null | undefined): boolean {
  return !!(_.isEmpty(str) || (str && /^\s*$/.test(str)));
}

function nonBlankOrUndefined(
  str: string | null | undefined
): string | undefined {
  return isBlank(str) ? undefined : _.trim(str || '');
}

interface HostedFieldProps {
  id: string;
  label: string;
  labelNote?: string;
  wrapperClassName?: string;
  error?: string;
}

const HostedField: React.FC<HostedFieldProps> = ({
  id,
  label,
  labelNote,
  wrapperClassName,
  error,
}) => {
  return (
    <div className={[styles.hostedFieldWrapper, wrapperClassName].join(' ')}>
      <label htmlFor={id}>
        <span className={styles.hostedFieldLabelWrapper}>
          <span className={styles.hostedFieldLabel}>{label}</span>
          {labelNote && (
            <span className={styles.hostedFieldLabelNote}>
              {' '}
              &mdash; {labelNote}
            </span>
          )}
        </span>
        <div
          className={[
            styles.hostedField,
            error ? styles.hostedFieldWithError : '',
          ].join(' ')}
          id={id}
        ></div>
      </label>
      <span className={styles.hostedFieldError}>{error}&nbsp;</span>
    </div>
  );
};

interface HostedFieldsFix {
  off: (
    event: string,
    handler: (event: HostedFieldsStateObject) => void
  ) => void;
}

interface PhysicianSpecialtyModalData {
  onClose: () => void;
  updateQty: (panelType: PanelType, qty: number, specialty?: number) => void;
}

interface LabelMaps {
  [key: string]: string;
}

const PhysicianSpecialtyModal: React.FC<PhysicianSpecialtyModalData> = ({
  onClose,
}) => {
  const [order, setOrder] = useState<Order>([]);
  const [labelMaps, setLabelMaps] = useState<LabelMaps>({});
  const [specialties, setSpecialties] = useState<OrderItem[]>([]);

  const hanldeChangeSpecialty = (
    specialty: number | undefined,
    value: number
  ) => {
    const orderItem = order.filter(i => i.specialty === specialty);
    const restItems = order.filter(i => i.specialty !== specialty);
    // if (!_.isEmpty(orderItem)) {
    //   updateQty(
    //     orderItem[0].panelType as PanelType,
    //     value,
    //     orderItem[0].specialty
    //   );
    // }
    const newOrderItem = {
      panelType: orderItem[0].panelType,
      specialty,
      quantity: value,
    };
    const newOrder: Order = [newOrderItem, ...restItems];
    setOrder(newOrder);
    saveOrder(newOrder);
  };

  useEffect(() => {
    const order = getOrder() || [];
    setOrder(order || []);
    const maps: LabelMaps = {};
    Object.entries(physicianOptions.QHCPSpecialty).map(([k, v]) => {
      maps[v] = k;
    });
    setLabelMaps(maps);
  }, []);

  useEffect(() => {
    const ss = order.filter(i => !_.isNil(i.specialty));
    if (!_.isEmpty(ss)) setSpecialties(ss);
  }, [order]);

  const handleClose = () => {
    //    let total = 0;
    //    const newPhysicanSpecialty: PhysicanSpecialty = {};
    //    specialty.forEach(([k, v]) => {
    //      newPhysicanSpecialty[k] = v;
    //      total += v;
    //    });
    //    updateQty(PanelType.PhysicianSurvey, total);
    //    savePhysicanSpecialty(newPhysicanSpecialty);
    onClose();
  };

  return (
    <Modal onClose={handleClose}>
      <div className={styles.modalPhysicianSpecial}>
        <h2>Physician Specialty</h2>
        <ul className={styles.listPhysicianSpecial}>
          {specialties.map(x => (
            <li
              className={styles.itemPhysicianSpecial}
              key={`${x.specialty}${x.quantity}`}
            >
              <h3 className={styles.labelPhysicianSpecial}>
                {x.specialty ? labelMaps[x.specialty] : ''}
              </h3>
              <div className={styles.wrapPhysicianSpecial}>
                <Dropdown
                  options={getQtyOptions(x.quantity)}
                  value={x.quantity}
                  onChange={value => hanldeChangeSpecialty(x.specialty, value)}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
};

const Purchase: React.FC<RouteComponentProps> = ({ history }) => {
  const surveySchema = useSurveyContext();
  const [step, setStep] = useState<PurchaseStep>(1);
  const setError = useErrorUpdateContext();
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [clientToken, setClientToken] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [btClient, setBtClient] = useState<braintree.Client | null>(null);
  const [
    btHostedFields,
    setBtHostedFields,
  ] = useState<braintree.HostedFields | null>(null);
  const [btHostedFieldsValidity, setBtHostedFieldsValidity] = useState<
    HostedFieldsValidity
  >({
    number: true,
    expirationDate: true,
    cvv: true,
    postalCode: true,
  });
  const [btNonce, setBtNonce] = useState<string | null>(null);

  const [order, setOrder] = useState<Order>([]);
  const [coupon, setCoupon] = useState<string | null>(null);
  const [discount, setDiscount] = useState(0.0);
  const [subtotal, setSubtotal] = useState(countTotal(order));
  const [total, setTotal] = useState(countTotal(order));
  const [isPhysicianSpecialtyOpen, setIsPhysicianSpecialtyOpen] = useState(
    false
  );
  const needPayment = useCallback(() => subtotal > 0 && subtotal > discount, [
    discount,
    subtotal,
  ]);

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [lastFourDigits, setLastFourDigits] = useState<string | null>(null);
  const smallScreen = useMediaQuery('(max-width: 820px)');
  const animateHeightDuration = 500;

  const initialValues: PurchaseFormValues = {
    cardholderName: config.type === 'demo' ? 'Nicole H' : '',
    streetAddress: '',
    company: '',
    locality: '',
    region: '',
    phone: '',
  };

  useEffect(() => {
    getClientToken().then(t => {
      if (t) {
        setClientToken(t);
      } else {
        setError({
          text: 'Failed to receive a client token for payment processing.',
        });
      }
    });
  }, [setError]);

  useEffect(() => {
    let order = getOrder();
    if (order) {
      setOrder(order);
    } else {
      history.push('/shop');
    }
    const maps: LabelMaps = {};
    Object.entries(physicianOptions.QHCPSpecialty).map(([k, v]) => {
      maps[v] = k;
    });
    setLabelMaps(maps);
  }, [history, isPhysicianSpecialtyOpen]);

  useEffect(() => {
    if (clientToken) {
      client.create({ authorization: clientToken }).then(client => {
        setBtClient(client);
        return hostedFields
          .create({
            client: client,
            styles: {
              input: {
                'font-size': '16px',
                'font-family':
                  'Suisse, HelveticaNeue, "Helvetica Neue", Helvetica, Arial, "Liberation Sans", FreeSans, clean, sans-serif',
                'font-weight': '400',
                'font-color': '#000000',
              },
              'input.invalid': {
                color: '#000000',
              },
              'input.valid': {
                color: '#000000',
              },
            },
            fields: {
              number: {
                selector: '#number',
                placeholder: '--',
                prefill:
                  config.type === 'demo' ? '4111111111111111' : undefined,
              },
              cvv: {
                selector: '#cvv',
                placeholder: '---',
                prefill: config.type === 'demo' ? '211' : undefined,
              },
              expirationDate: {
                selector: '#expirationDate',
                placeholder: '--/--',
                prefill: config.type === 'demo' ? '11/25' : undefined,
              },
              postalCode: {
                selector: '#postalCode',
                placeholder: '--',
                prefill: config.type === 'demo' ? '19067' : undefined,
              },
            },
          })
          .then(hostedFields => {
            setBtHostedFields(hostedFields);
          })
          .catch((e: RestError) => {
            setError({
              text: getErrorMessage(e),
            });
          });
      });
    }
  }, [clientToken, setError]);

  useEffect(() => {
    const listener = (event: HostedFieldsStateObject) => {
      const field = event.fields[event.emittedBy];
      setBtHostedFieldsValidity({
        ...btHostedFieldsValidity,
        [event.emittedBy]: field.isValid,
      });
    };

    if (btHostedFields) {
      btHostedFields.on('validityChange', listener);
    }

    return () => {
      if (btHostedFields) {
        ((btHostedFields as unknown) as HostedFieldsFix).off(
          'validityChange',
          listener
        );
      }
    };

    // Do not include `btHostedFieldsValidity` here, or you'll add a new event listener every time its value changes!
    // eslint-disable-next-line
  }, [btHostedFields]);

  const updateQty = useCallback(
    (itemType: PanelType, qty: number, specialty?: number) => {
      const restItems = order.filter(
        i => i.panelType !== itemType || i.specialty !== specialty
      );

      const s = specialty === 0 ? undefined : specialty;
      const newOrderItem = {
        panelType: itemType,
        specialty: s,
        quantity: qty,
      };

      const newOrder: Order = [newOrderItem, ...restItems];

      setOrder(newOrder);
      saveOrder(newOrder);
    },

    [order]
  );

  useEffect(() => {
    gtag('config', config.gaTrackingId, {
      // eslint-disable-next-line @typescript-eslint/camelcase
      page_path: history.location.pathname + '#' + stepUrlIds[step],
      currency: 'USD',
      value: step === 4 ? total : undefined,
    });
  }, [history.location.pathname, step, total]);

  const [labelMaps, setLabelMaps] = useState<LabelMaps>({});

  return (
    <DefaultPage
      header={{
        title: 'Purchase Panel Surveys',
        subtitle: 'This is your purchase checkout.',
        backAction: {
          text: 'Back to recommendations'.toUpperCase(),
          onCompletion: () => {
            sessionStorage.clear();
            history.push('/shop');
          },
        },
      }}
    >
      <Box>
        <div className={[styles.step, styles.step1].join(' ')}>
          <StepHeader
            title="Order Summary"
            isActive={step === 1}
            isComplete={step > 1}
            isEditable={step > 1 && step < 4}
            onEditClick={() => setStep(1)}
          />
          <AnimateHeight
            duration={animateHeightDuration}
            height={step === 1 ? 'auto' : 0}
          >
            <OrderTable
              smallScreen={smallScreen || false}
              order={order}
              updateQty={updateQty}
              subtotal={subtotal}
              setSubtotal={setSubtotal}
              discount={discount}
              setDiscount={setDiscount}
              total={total}
              setTotal={setTotal}
              setValidCoupon={setCoupon}
              setIsPhysicianSpecialtyOpen={setIsPhysicianSpecialtyOpen}
            />
            <div className={styles.buttons}>
              <ActionButton
                onClick={() => setStep(needPayment() ? 2 : 3)}
                type="primary"
                text="Next"
                disabled={subtotal === 0.0}
              />
            </div>
          </AnimateHeight>
        </div>
      </Box>
      <Box>
        <div className={[styles.step, styles.step2].join(' ')}>
          <StepHeader
            title="Payment Details"
            isActive={step === 2}
            isComplete={step > 2}
            isEditable={step > 2 && step < 4 && needPayment()}
            onEditClick={() => setStep(2)}
          />
          <AnimateHeight
            duration={animateHeightDuration}
            height={step === 2 ? 'auto' : 0}
          >
            <Formik
              initialValues={initialValues}
              validationSchema={purchaseFormValidationSchema}
              onSubmit={(values, { setSubmitting }) => {
                if (btClient && btHostedFields) {
                  const formState = btHostedFields.getState();
                  setBtHostedFieldsValidity({
                    number:
                      formState.fields.number &&
                      formState.fields.number.isValid,
                    expirationDate:
                      formState.fields.expirationDate &&
                      formState.fields.expirationDate.isValid,
                    cvv: formState.fields.cvv && formState.fields.cvv.isValid,
                    postalCode:
                      formState.fields.postalCode &&
                      formState.fields.postalCode.isValid,
                  });
                  setSubmitting(true);
                  setError(null);
                  btHostedFields
                    .tokenize({
                      cardholderName: _.trim(values.cardholderName),
                      billingAddress: {
                        streetAddress: nonBlankOrUndefined(
                          values.streetAddress
                        ),
                        company: nonBlankOrUndefined(values.company),
                        locality: nonBlankOrUndefined(values.locality),
                        region: nonBlankOrUndefined(values.region),
                      },
                    })
                    .then(payload => {
                      setBtNonce(payload.nonce);
                      setSubmitting(false);
                      setCompanyName(values.company);
                      setLastFourDigits(payload.details.lastFour);
                      setStep(3);
                    })
                    .catch((e: RestError) => {
                      setError({
                        text: getErrorMessage(e),
                      });
                      setSubmitting(false);
                    });
                }
              }}
            >
              {({ handleSubmit, isSubmitting }) => (
                <form onSubmit={handleSubmit}>
                  {!btHostedFields && <Loading paddingVertical={32} />}
                  <div
                    className={styles.formItems}
                    style={{
                      // `display: none;` prevents broken DOM references when working with Braintree APIs
                      display: step === 2 && btHostedFields ? 'block' : 'none',
                    }}
                  >
                    <h5 className={styles.stepSubtitle}>Payment Details</h5>
                    <div className={styles.cardIcons}>
                      <img src={visa} alt="Visa icon" aria-hidden={true} />
                      <img
                        src={mastercard}
                        alt="MasterCard icon"
                        aria-hidden={true}
                      />
                      <img
                        src={americanExpress}
                        alt="American Express icon"
                        aria-hidden={true}
                      />
                      <img src={cjb} alt="JCB icon" aria-hidden={true} />
                      <img
                        src={discover}
                        alt="Discover icon"
                        aria-hidden={true}
                      />
                    </div>
                    <div
                      className={[
                        styles.formItemFullWidth,
                        styles.formRow,
                      ].join(' ')}
                    >
                      <HostedField
                        id="number"
                        label="Card Number"
                        labelNote="required"
                        wrapperClassName={styles.formItemMedium}
                        error={
                          btHostedFieldsValidity.number
                            ? undefined
                            : 'Card number is not valid'
                        }
                      />
                      <HostedField
                        id="cvv"
                        label="CVV"
                        labelNote="required"
                        wrapperClassName={styles.formItemSmall}
                        error={
                          btHostedFieldsValidity.cvv
                            ? undefined
                            : 'CVV is not valid'
                        }
                      />
                      <HostedField
                        id="expirationDate"
                        label="EXP"
                        labelNote="required"
                        wrapperClassName={styles.formItemSmall}
                        error={
                          btHostedFieldsValidity.expirationDate
                            ? undefined
                            : 'Expiration date is not valid'
                        }
                      />
                    </div>
                    <TextField
                      name="cardholderName"
                      label="Name on Card"
                      labelNote="required"
                      variant="thick"
                      wrapperClassName={styles.formItemBig}
                    />
                    <h5 className={styles.stepSubtitle}>Billing Address</h5>
                    <TextField
                      name="streetAddress"
                      label="Street Address"
                      variant="thick"
                      wrapperClassName={styles.formItemBig}
                    />
                    <TextField
                      name="company"
                      label="Company Name"
                      variant="thick"
                      wrapperClassName={styles.formItemBig}
                    />
                    <TextField
                      name="locality"
                      label="Town/City"
                      variant="thick"
                      wrapperClassName={styles.formItemBig}
                    />
                    <div className={styles.formRow}>
                      <TextField
                        name="region"
                        label="State"
                        variant="thick"
                        wrapperClassName={styles.formItemSmall}
                      />
                      <HostedField
                        id="postalCode"
                        label="Zip Code"
                        labelNote="required"
                        wrapperClassName={styles.formItemSmaller}
                        error={
                          btHostedFieldsValidity.postalCode
                            ? undefined
                            : 'Zip code is not valid'
                        }
                      />
                    </div>
                  </div>
                  <div className={styles.buttons}>
                    <ActionButton
                      onClick={() => setStep(1)}
                      type="secondary"
                      text="Back"
                    />
                    <ActionButton
                      onClick={() => !isSubmitting && handleSubmit()}
                      type="primary"
                      text="Next"
                      disabled={!btClient}
                      loading={isSubmitting}
                    />
                  </div>
                </form>
              )}
            </Formik>
          </AnimateHeight>
        </div>
      </Box>
      <Box>
        <div className={[styles.step, styles.step3].join(' ')}>
          <StepHeader
            title="Review & Place Order"
            isActive={step === 3}
            isComplete={step > 3}
            isEditable={false}
            onEditClick={() => setStep(3)}
          />
          <AnimateHeight
            duration={animateHeightDuration}
            height={step === 3 ? 'auto' : 0}
          >
            <div className={styles.step3Content}>
              <h5 className={styles.stepSubtitle}>Details</h5>
              {companyName && (
                <p>
                  <strong>Company Name:</strong>
                  &nbsp; {companyName}
                </p>
              )}
              {lastFourDigits && (
                <p>
                  <strong>Purchase to Card Number:</strong>
                  &nbsp; XXXX XXXX XXXX {lastFourDigits}
                </p>
              )}
              <div className={styles.step3TableWrapper}>
                <table className={styles.purchaseTable}>
                  <thead>
                    {smallScreen === true ? (
                      <tr>
                        <th colSpan={4} className={styles.purchaseTableTh}>
                          Panel Surveys
                        </th>
                      </tr>
                    ) : (
                      <tr>
                        <th className={styles.purchaseTableTh}>
                          Panel Surveys
                        </th>
                        <th
                          style={{ width: 120 }}
                          className={styles.purchaseTableTh}
                        >
                          Price
                        </th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {order.map(i => (
                      <tr key={`${i.panelType} ${i.specialty}`}>
                        <td>
                          <p className={styles.tableProductTitle}>
                            ({i.quantity}){' '}
                            {getPanelTypeText(i.panelType as PanelType) +
                              ' Panel Survey' +
                              (i.specialty
                                ? ` - ${labelMaps[i.specialty || 0]}`
                                : '')}
                          </p>
                          <p className={styles.tableProductSubtitle}>
                            For{' '}
                            {getPanelTypeText(i.panelType as PanelType) +
                              ' Panel'}
                          </p>
                        </td>
                        <td>
                          <span className={styles.tableProductPrice}>
                            $
                            {formatUSD(
                              products[i.panelType as PanelType].price
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className={styles.tableFooter}>
                    <tr>
                      <td className={styles.tableAlignRight}>
                        <span className={styles.tableFooterTitle}>
                          Subtotal
                        </span>
                      </td>
                      <td>
                        <span className={styles.tableFooterValue}>
                          ${formatUSD(subtotal)}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.tableAlignRight}>
                        <span className={styles.tableFooterTitle}>
                          Discount
                        </span>
                      </td>
                      <td>
                        <span className={styles.tableFooterValue}>
                          ${formatUSD(discount)}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.tableAlignRight}>
                        <span className={styles.tableFooterTitleBig}>
                          Total
                        </span>
                      </td>
                      <td>
                        <span className={styles.tableFooterValueBig}>
                          ${formatUSD(total)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className={styles.termsWrapper}>
                <span>
                  Please review and click Continue when you are ready to
                  proceed.
                  <br />
                  <br />
                  Restrictions of use:
                </span>
                <ul>
                  <li>
                    Surveys purchased on or before Sept 30 must be launched
                    (submitted and sent to panelists) on or before Dec 15th of
                    the <u>same</u> year; otherwise the survey will expire and
                    be removed from the dashboard. Surveys purchased after Sept
                    30th must be launched (submitted and sent to panelists) on
                    or before Dec 15th of the <u>next</u> year; otherwise the
                    survey will expire and be removed from the dashboard. There
                    will be no refund or credit for expired surveys.
                  </li>
                  <li>
                    At time of survey expiration, expired surveys will be
                    removed from the Customer’s dashboard. Should an extension
                    on survey expiration date be required, the Customer must
                    contact EVERSANA with a request to extend the timeframe.
                  </li>
                  <li>
                    This contract is not transferable to another company.
                    EVERSANA may refuse transfer of surveys to any department,
                    users, or affiliates of customer at its sole discretion.
                  </li>
                  <li>
                    EVERSANA will utilize best efforts to achieve the target
                    sample for projects and within the timeline requested;
                    however, due to the availability of executives we cannot
                    guarantee the final sample size requested will be achieved
                    within the outlined timeframe. If this occurs EVERSANA and
                    Customer will discuss options for the research. EVERSANA and
                    the Customer must agree in writing to any final disposition
                  </li>
                  <li>
                    The nature in which the Customer poses survey questions
                    shall govern the accuracy of the survey results; EVERSANA
                    shall not be liable for poor or inaccurate survey results
                    due to ambiguous or otherwise confusing survey questions.
                  </li>
                  <li>
                    <b>ACCESS</b>EXPRESS users are the sole owners of their
                    respective survey responses, and as such, each user is
                    solely responsible for complying with all applicable
                    reporting obligations, including but not limited to adverse
                    event and/or product complaint reporting, triggered by the
                    survey responses. For clarity, EVERSANA shall not be liable
                    for a user’s failure to comply with its reporting
                    obligations.
                  </li>
                </ul>
                <button
                  className={styles.termsLink}
                  onClick={() => {
                    window.open(
                      `${config.publicUrl}/termsAndConditions`,
                      '_blank'
                    );
                  }}
                >
                  Click to Review Terms and Conditions
                </button>
              </div>
            </div>
            <div className={styles.buttons}>
              <ActionButton
                onClick={() => setStep(needPayment() ? 2 : 1)}
                type="secondary"
                text="Back"
              />
              <ActionButton
                type="primaryAlt"
                text="Place order"
                disabled={(needPayment() && !btNonce) || purchaseComplete}
                loading={isProcessingPayment}
                onClick={() => {
                  if (isProcessingPayment) {
                    return;
                  }
                  if (order && (needPayment() ? btNonce : true)) {
                    setIsProcessingPayment(true);
                    setError(null);
                    createPendingPurchase(order, coupon || undefined)
                      .then(pendingPurchaseId =>
                        payPurchase(pendingPurchaseId, btNonce || undefined)
                      )
                      .then(purchaseResult => {
                        setIsProcessingPayment(false);
                        if (purchaseResult.toLowerCase() === 'successful') {
                          setStep(4);
                          setPurchaseComplete(true);
                        }
                      })
                      .catch((e: RestError) => {
                        setIsProcessingPayment(false);
                        setError({
                          text: getErrorMessage(e),
                          onClose: () => setStep(2),
                        });
                      });
                  }
                }}
              />
            </div>
          </AnimateHeight>
        </div>
      </Box>
      {purchaseComplete ? (
        <div className={styles.purchaseComplete}>
          <h5 className={styles.purchaseCompleteTitle}>
            Thank you for your panel survey purchase!
          </h5>
          <ActionButton
            text="To begin your survey, please proceed with the survey creation process. If you have already created your survey, kindly proceed to launch it."
            type="primary"
            onClick={() => {
              const mySet = new Set();
              order.forEach(element => {
                mySet.add(element.panelType);
              });
              if (mySet.size === 1 && surveySchema) {
                if (
                  surveySchema.status == 'ReadyToSend' &&
                  order[0].panelType == surveySchema.panel
                ) {
                  history.push('/PS');
                } else {
                  history.push('/dashboard');
                }
              } else {
                history.push('/dashboard');
              }
            }}
          />

          <p className={styles.orText}>or</p>
          <p>
            <strong>Come back later and log into your account.</strong>
          </p>
          <p>
            Once you log in, please proceed to your dashboard — there you will
            see your panel survey purchases. You can then create your survey
            from one of our templates or from scratch.
          </p>
        </div>
      ) : null}
      {isPhysicianSpecialtyOpen && (
        <PhysicianSpecialtyModal
          updateQty={(k, v, s) => updateQty(k, v, s)}
          onClose={() => setIsPhysicianSpecialtyOpen(false)}
        />
      )}
    </DefaultPage>
  );
};

export default withRouter(Purchase);
